// Approachable patches of warmth, never a pickup or a score.
// Independent deterministic RNG: adding lights does not reshuffle peers/rocks.
import {applyMoodEvent} from './mood-system.js';
import {underCanopy} from './weather-system.js';
export const WARM_LIGHT = Object.freeze({radius:.95,dwell:3,firstDelay:3,interval:8,lifetime:30,fade:.85,drift:.24,bound:2.48});
const clamp=(x,a,b)=>Math.max(a,Math.min(b,x));
const finite=(x,f=0)=>Number.isFinite(x)?x:f;
const playable=mode=>['paths','relationships','emotions','discomfort','resilience'].includes(mode);
function random(state){state.seed=(state.seed*16807)%2147483647;return(state.seed-1)/2147483646;}
function position(patch,reduced=false){
 const t=reduced?0:patch.age*.16;
 return {x:patch.baseX+Math.sin(t+patch.phase)*WARM_LIGHT.drift,
  z:patch.baseZ+Math.cos(t*.83+patch.phase)*WARM_LIGHT.drift*.7};
}
export function createWarmLight(saved={}){
 saved=saved&&typeof saved==='object'?saved:{};
 const state={seed:clamp(Math.floor(finite(saved.seed,73991)),1,2147483646),
  delay:clamp(finite(saved.delay,WARM_LIGHT.firstDelay),0,WARM_LIGHT.interval),
  serial:Math.max(0,Math.floor(finite(saved.serial))),claimed:Math.max(0,Math.floor(finite(saved.claimed))),patch:null};
 const s=saved.patch;
 if(s&&Number.isFinite(s.id)&&s.id>0&&Number.isFinite(s.baseX)&&Number.isFinite(s.baseZ)){
  state.patch={id:Math.floor(s.id),baseX:clamp(s.baseX,-WARM_LIGHT.bound,WARM_LIGHT.bound),baseZ:clamp(s.baseZ,-WARM_LIGHT.bound,WARM_LIGHT.bound),
   phase:finite(s.phase),age:clamp(finite(s.age),0,WARM_LIGHT.lifetime),dwell:clamp(finite(s.dwell),0,WARM_LIGHT.dwell),
   consumed:!!s.consumed,retiring:!!s.retiring,alpha:clamp(finite(s.alpha),0,1)};
  state.serial=Math.max(state.serial,state.patch.id);
 }
 return state;
}
function clearOfCanopy(x,z,radius){
 if(underCanopy(x,z,0))return false;
 for(let i=0;i<8;i++)if(underCanopy(x+Math.cos(i*Math.PI/4)*radius,z+Math.sin(i*Math.PI/4)*radius,0))return false;
 return true;
}
function spawn(state,ball,isSafe){
 for(let i=0;i<36;i++){
  const patch={id:state.serial+1,baseX:(random(state)*2-1)*WARM_LIGHT.bound,baseZ:(random(state)*2-1)*WARM_LIGHT.bound,
   phase:random(state)*Math.PI*2,age:0,dwell:0,consumed:false,retiring:false,alpha:0};
  const p=position(patch);
  // A stationary ball cannot farm new patches: each appears away from it.
  if(Math.hypot(p.x-ball.x,p.z-ball.z)<WARM_LIGHT.radius+ball.radius+.48)continue;
  if(!clearOfCanopy(p.x,p.z,WARM_LIGHT.radius+WARM_LIGHT.drift)||!isSafe(p.x,p.z,WARM_LIGHT.radius+WARM_LIGHT.drift))continue;
  state.patch=patch;state.serial++;return true;
 }
 state.delay=2;return false; // Retry later; never force an unreachable location.
}
export function warmLightSnapshot(state,{weather='sunshine',mode='paths',ball={x:0,z:0,radius:.225},reduced=false}={}){
 const patch=state.patch;if(!patch)return null;
 const p=position(patch,reduced),inside=Math.hypot(p.x-ball.x,p.z-ball.z)+ball.radius<=WARM_LIGHT.radius;
 return {...p,id:patch.id,radius:WARM_LIGHT.radius,alpha:patch.alpha,progress:patch.dwell/WARM_LIGHT.dwell,
  consumed:patch.consumed,inside,eligible:playable(mode)&&weather!=='rain'&&!patch.consumed&&!patch.retiring,age:patch.age};
}
export function updateWarmLight(state,mood,{dt=0,weather='sunshine',mode='paths',ball={x:0,z:0,radius:.225},isSafe=()=>true,reduced=false}={}){
 dt=clamp(finite(dt),0,.1);if(dt===0)return null;
 const enabled=playable(mode),dry=weather==='sunshine'||weather==='breeze';
 if(!state.patch){
  if(enabled)state.delay=Math.max(0,state.delay-dt);
  if(enabled&&dry&&state.delay<=1e-7)spawn(state,ball,isSafe);
  return null;
 }
 const patch=state.patch,active=enabled&&dry;
 if(active&&!patch.retiring){patch.age+=dt;if(patch.age>=WARM_LIGHT.lifetime)patch.retiring=true;}
 const p=position(patch,reduced);
 if(active&&!patch.retiring&&!isSafe(p.x,p.z,WARM_LIGHT.radius)){patch.retiring=true;patch.dwell=0;}
 const visible=active&&!patch.retiring&&!patch.consumed;
 patch.alpha=clamp(patch.alpha+(visible?dt/.65:-dt/WARM_LIGHT.fade),0,1);
 if(patch.retiring||patch.consumed){
  if(patch.alpha<=1e-7){state.patch=null;state.delay=WARM_LIGHT.interval;}
  return null;
 }
 const inside=Math.hypot(p.x-ball.x,p.z-ball.z)+ball.radius<=WARM_LIGHT.radius;
 // Three continuous foreground seconds with the entire ball in the light.
 if(active&&inside&&patch.alpha>.55)patch.dwell+=dt;else patch.dwell=0;
 if(patch.dwell+1e-7<WARM_LIGHT.dwell)return null;
 patch.dwell=WARM_LIGHT.dwell;patch.consumed=true;state.claimed++;
 applyMoodEvent(mood,'warmth',{cooldown:false});
 return {type:'warmth',x:ball.x,z:ball.z,strength:.65,lightId:patch.id};
}
export function serializeWarmLight(state){return {version:1,seed:state.seed,delay:state.delay,serial:state.serial,claimed:state.claimed,patch:state.patch?{...state.patch}:null};}
